/**
 * IndustryDebtEquityChart
 *
 * Horizontal bar chart showing Debt/Equity ratio per company for a selected year.
 * Mirrors the FID DebtEquityChart at industry level with per-company comparison.
 * Uses YearNavigator for year selection.
 */
import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear } from '../../financial/utils/formatters';
import YearNavigator from './YearNavigator';
import type { RegionConfig } from '../../../types/financial';
import type { CompanyTimeSeries, IndustryBenchmarkRecord } from '../../../types/industryResearch';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

interface Props {
  companies: CompanyTimeSeries[];
  medianData: IndustryBenchmarkRecord[];
  region?: RegionConfig;
}

export default function IndustryDebtEquityChart({ companies, medianData, region }: Props) {
  const years = medianData.map((d) => d.year).sort((a, b) => a - b);
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1] ?? 0);

  const barData = companies.map((co, i) => {
    const rec = co.data.find((d) => d.year === selectedYear);
    let ratio: number | null = rec?.debt_equity_ratio ?? null;
    if (ratio == null && rec) {
      const equity = rec.shareholders_funds;
      const debt = rec.total_liabilities ?? (((rec.total_current_liabilities ?? 0) + (rec.total_non_current_liabilities ?? 0)) || null);
      if (debt != null && equity != null && equity !== 0) {
        ratio = parseFloat((debt / equity).toFixed(3));
      }
    }
    return {
      name: co.company_name.length > 22 ? co.company_name.slice(0, 20) + '...' : co.company_name,
      fullName: co.company_name,
      value: ratio,
      color: COLORS[i % COLORS.length],
    };
  })
  .filter((d) => d.value != null)
  .sort((a, b) => (a.value ?? 0) - (b.value ?? 0)); // lower D/E first (less leveraged)

  if (barData.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">Debt / Equity by Company</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Total liabilities / shareholders equity — lower is less leveraged</p>
          </div>
          <YearNavigator
            years={years}
            selectedYear={selectedYear}
            onChange={setSelectedYear}
            formatYear={(y) => formatFiscalYear(y, region) ?? `FY${y}`}
          />
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 48 + 30)}>
          <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 50, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.toFixed(2)} domain={[0, 'auto']} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(v: number) => [v?.toFixed(3), 'D/E Ratio']}
              labelFormatter={(l) => barData.find(d => d.name === l)?.fullName ?? l}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={26}
              label={{ position: 'right', fontSize: 10, fill: '#6b7280', formatter: (v: number) => v != null ? v.toFixed(2) : '' }}>
              {barData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
